import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { Button, ConfirmModal, Input, ModalShell } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type HolidayType = 'inamovible' | 'trasladable' | 'puente' | 'otro';
const HOLIDAY_TYPES: HolidayType[] = ['inamovible', 'trasladable', 'puente', 'otro'];

type Holiday = {
  id: number;
  fecha: string;
  nombre: string;
  tipo: HolidayType;
  origen: 'manual' | 'importado';
  updatedAt: string;
  updatedBy: string | null;
};

type ImportItem = {
  fecha: string;
  nombre: string;
  tipo: HolidayType;
  yaCargado: boolean;
  nombreCargado: string | null;
};

function apiErrorMessage(err: unknown): string {
  const axiosMsg =
    err && typeof err === 'object' && 'response' in err
      ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
      : undefined;
  return axiosMsg || (err instanceof Error ? err.message : i18next.t('common.error'));
}

/** 'YYYY-MM-DD' → "sábado, 10 de octubre" en el idioma del Panel (sin corrimiento de zona horaria). */
function formatHolidayDate(iso: string, language: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(language, {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function isWeekend(iso: string): boolean {
  const [y, m, d] = iso.split('-').map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return wd === 0 || wd === 6;
}

const typeBadge: Record<HolidayType, string> = {
  inamovible: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
  trasladable: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  puente: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
  otro: 'bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-slate-300',
};

const selectClass =
  'block h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

export const HolidaysPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { canAdminister } = useAuth();
  const { success, error: showError } = useToast();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [isLoading, setIsLoading] = useState(true);
  const [holidays, setHolidays] = useState<Holiday[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Holiday | null>(null);
  const [formDate, setFormDate] = useState('');
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<HolidayType>('otro');
  const [isSaving, setIsSaving] = useState(false);

  const [deleting, setDeleting] = useState<Holiday | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [importItems, setImportItems] = useState<ImportItem[]>([]);
  const [importSelected, setImportSelected] = useState<Set<string>>(new Set());
  const [isLoadingImport, setIsLoadingImport] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const load = useCallback(async (y: number) => {
    setIsLoading(true);
    try {
      const res = await httpClient.get<Holiday[]>(`/panel-config/feriados?year=${y}`);
      setHolidays(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showError(apiErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load(year);
  }, [load, year]);

  const years = useMemo(() => [currentYear - 1, currentYear, currentYear + 1, currentYear + 2], [currentYear]);

  const openCreate = () => {
    setEditing(null);
    setFormDate(`${year}-01-01`);
    setFormName('');
    setFormType('otro');
    setShowForm(true);
  };

  const openEdit = (h: Holiday) => {
    setEditing(h);
    setFormDate(h.fecha);
    setFormName(h.nombre);
    setFormType(h.tipo);
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdminister) return;
    if (!formDate) {
      showError(t('pages.holidays.dateRequired'));
      return;
    }
    if (!formName.trim()) {
      showError(t('pages.holidays.nameRequired'));
      return;
    }
    setIsSaving(true);
    try {
      const body = { fecha: formDate, nombre: formName.trim(), tipo: formType };
      if (editing) {
        await httpClient.put(`/panel-config/feriados/${editing.id}`, body);
        success(i18next.t('pages.holidays.updated'));
      } else {
        await httpClient.post('/panel-config/feriados', body);
        success(i18next.t('pages.holidays.created'));
      }
      setShowForm(false);
      const savedYear = Number(formDate.slice(0, 4));
      if (savedYear !== year) setYear(savedYear);
      else await load(year);
    } catch (err) {
      showError(apiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await httpClient.delete(`/panel-config/feriados/${deleting.id}`);
      success(i18next.t('pages.holidays.deleted'));
      setDeleting(null);
      await load(year);
    } catch (err) {
      showError(apiErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const openImport = async () => {
    setShowImport(true);
    setImportItems([]);
    setImportSelected(new Set());
    setIsLoadingImport(true);
    try {
      const res = await httpClient.get<ImportItem[]>(`/panel-config/feriados/importar?year=${year}`, {
        timeout: 20000,
      });
      const items = Array.isArray(res.data) ? res.data : [];
      setImportItems(items);
      setImportSelected(new Set(items.filter((i) => !i.yaCargado).map((i) => i.fecha)));
    } catch (err) {
      showError(apiErrorMessage(err));
      setShowImport(false);
    } finally {
      setIsLoadingImport(false);
    }
  };

  const toggleImport = (fecha: string) => {
    setImportSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fecha)) next.delete(fecha);
      else next.add(fecha);
      return next;
    });
  };

  const handleImport = async () => {
    const items = importItems
      .filter((i) => importSelected.has(i.fecha) && !i.yaCargado)
      .map(({ fecha, nombre, tipo }) => ({ fecha, nombre, tipo }));
    if (items.length === 0) {
      showError(i18next.t('pages.holidays.importNothingSelected'));
      return;
    }
    setIsImporting(true);
    try {
      const res = await httpClient.post<{ created: Holiday[]; skipped: string[] }>(
        '/panel-config/feriados/importar',
        { items }
      );
      success(i18next.t('pages.holidays.imported', { count: res.data.created.length }));
      setShowImport(false);
      await load(year);
    } catch (err) {
      showError(apiErrorMessage(err));
    } finally {
      setIsImporting(false);
    }
  };

  const selectableCount = importItems.filter((i) => !i.yaCargado && importSelected.has(i.fecha)).length;

  return (
    <div className="panel-page h-full min-w-0 flex flex-col overflow-auto">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.holidays.title')}</h1>
          <p className="panel-page-subtitle">{t('pages.holidays.subtitle')}</p>
        </div>
        {canAdminister ? (
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => void openImport()}>
              {t('pages.holidays.importButton', { year })}
            </Button>
            <Button type="button" size="sm" onClick={openCreate}>
              {t('common.create')}
            </Button>
          </div>
        ) : null}
      </div>

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400" htmlFor="holidays-year">
            {t('pages.holidays.year')}
          </label>
          <select
            id="holidays-year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className={`${selectClass} h-8 w-28 text-xs`}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {t('pages.holidays.weekendNote')}
          </span>
        </div>

        <div className="overflow-auto rounded-lg border border-slate-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-gray-900 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2">{t('pages.holidays.date')}</th>
                <th className="px-3 py-2">{t('pages.holidays.name')}</th>
                <th className="px-3 py-2">{t('pages.holidays.type')}</th>
                <th className="px-3 py-2">{t('pages.holidays.origin')}</th>
                <th className="px-3 py-2 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : holidays.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                    {t('pages.holidays.empty', { year })}
                  </td>
                </tr>
              ) : (
                holidays.map((h) => (
                  <tr key={h.id} className="border-t border-slate-100 dark:border-gray-700">
                    <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-900 dark:text-white">
                      {formatHolidayDate(h.fecha, i18n.language)}
                      {isWeekend(h.fecha) ? (
                        <span className="ml-2 text-xs font-normal text-slate-400">
                          {t('pages.holidays.weekendTag')}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{h.nombre}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${typeBadge[h.tipo]}`}>
                        {t(`pages.holidays.types.${h.tipo}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                      {t(`pages.holidays.origins.${h.origen}`)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={!canAdminister}
                          onClick={() => openEdit(h)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={!canAdminister}
                          onClick={() => setDeleting(h)}
                        >
                          {t('pages.holidays.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <ModalShell
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? t('pages.holidays.editTitle') : t('pages.holidays.createTitle')}
        subtitle={t('pages.holidays.modalSubtitle')}
        size="md"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="holiday-form" disabled={!canAdminister || isSaving} isLoading={isSaving}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <form id="holiday-form" onSubmit={handleSave} className="space-y-4">
          <Input
            label={t('pages.holidays.date')}
            type="date"
            value={formDate}
            onChange={(e) => setFormDate(e.target.value)}
            disabled={!canAdminister || isSaving}
          />
          <Input
            label={t('pages.holidays.name')}
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder={t('pages.holidays.namePlaceholder')}
            disabled={!canAdminister || isSaving}
            maxLength={200}
            autoFocus
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="holiday-type">
              {t('pages.holidays.type')}
            </label>
            <select
              id="holiday-type"
              value={formType}
              onChange={(e) => setFormType(e.target.value as HolidayType)}
              className={selectClass}
              disabled={!canAdminister || isSaving}
            >
              {HOLIDAY_TYPES.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {t(`pages.holidays.types.${tipo}`)}
                </option>
              ))}
            </select>
          </div>
        </form>
      </ModalShell>

      <ModalShell
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        title={t('pages.holidays.importTitle', { year })}
        subtitle={t('pages.holidays.importSubtitle')}
        size="lg"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setShowImport(false)} disabled={isImporting}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => void handleImport()}
              disabled={!canAdminister || isImporting || isLoadingImport || selectableCount === 0}
              isLoading={isImporting}
            >
              {t('pages.holidays.importConfirm', { count: selectableCount })}
            </Button>
          </>
        }
      >
        {isLoadingImport ? (
          <p className="py-6 text-center text-sm text-slate-500">{t('common.loading')}</p>
        ) : importItems.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">{t('pages.holidays.importEmpty')}</p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-gray-700">
            {importItems.map((item) => (
              <li key={item.fecha} className="flex items-start gap-3 py-2">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  checked={item.yaCargado || importSelected.has(item.fecha)}
                  disabled={item.yaCargado}
                  onChange={() => toggleImport(item.fecha)}
                  aria-label={item.nombre}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 dark:text-white">
                    {formatHolidayDate(item.fecha, i18n.language)}
                    <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${typeBadge[item.tipo]}`}>
                      {t(`pages.holidays.types.${item.tipo}`)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{item.nombre}</div>
                  {item.yaCargado ? (
                    <div className="text-xs text-slate-400">
                      {t('pages.holidays.importAlreadyLoaded', { name: item.nombreCargado })}
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </ModalShell>

      <ConfirmModal
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => handleDelete()}
        title={t('pages.holidays.confirmDeleteTitle')}
        message={t('pages.holidays.confirmDeleteMessage', {
          name: deleting?.nombre,
          date: deleting ? formatHolidayDate(deleting.fecha, i18n.language) : '',
        })}
        confirmText={t('pages.holidays.delete')}
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};
